import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

/* Admin Access — roles and per-module grants for scoped platform admins.
 *
 * Two different base paths sit behind one service: the management endpoints
 * live under /api/admin-access (super_admin only), while "what may I see"
 * lives under /api/users/me/permissions, because it answers for every signed-in
 * role rather than only for admins. baseUrl is therefore the API root and each
 * endpoint gives its full path.
 */
export const adminAccessApi = createApi({
    reducerPath: 'adminAccessApi',
    baseQuery: fetchBaseQuery({
        baseUrl: `${process.env.REACT_APP_BACKEND_HOST}/api`,
        prepareHeaders: (headers) => {
            const token = localStorage.getItem('prona_userToken');
            if (token) headers.set('Authorization', `Bearer ${token}`);
            return headers;
        },
    }),
    tagTypes: ['AdminUsers', 'MyPermissions', 'AdminModules'],
    endpoints: (build) => ({
        getMyPermissions: build.query({
            query: () => '/users/me/permissions',
            providesTags: ['MyPermissions'],
        }),

        getAdminModules: build.query({
            query: () => '/admin-access/modules',
            providesTags: ['AdminModules'],
        }),

        getAdminUsers: build.query({
            query: () => '/admin-access/users',
            providesTags: ['AdminUsers'],
        }),

        searchUsers: build.query({
            query: (email) => `/admin-access/search?email=${encodeURIComponent(email || '')}`,
        }),

        setAdminAccess: build.mutation({
            query: ({ userId, role_type, permissions }) => ({
                url: `/admin-access/users/${userId}`,
                method: 'POST',
                body: { role_type, permissions },
            }),
            /* MyPermissions too: editing your own row is refused by the server,
               but editing someone else's can still change what THEY see, and a
               super_admin often has the screen open in another tab. */
            invalidatesTags: ['AdminUsers', 'MyPermissions'],
        }),

        revokeAdminAccess: build.mutation({
            query: (userId) => ({
                url: `/admin-access/users/${userId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['AdminUsers', 'MyPermissions'],
        }),

        /* Whether this admin receives the mail the platform sends to staff.
           Only 'AdminUsers' is invalidated: the flag changes no permission, so
           re-fetching MyPermissions would be a request for nothing. */
        setAdminEmailNotifications: build.mutation({
            query: ({ userId, enabled }) => ({
                url: `/admin-access/users/${userId}/email-notifications`,
                method: 'PUT',
                body: { email_notifications: !!enabled },
            }),
            invalidatesTags: ['AdminUsers'],
        }),
    }),
});

export const {
    useGetMyPermissionsQuery,
    useGetAdminModulesQuery,
    useGetAdminUsersQuery,
    useLazySearchUsersQuery,
    useSetAdminAccessMutation,
    useRevokeAdminAccessMutation,
    useSetAdminEmailNotificationsMutation,
} = adminAccessApi;
